// Los seis tonos de avatar de la suite (`--color-avatar-*` en tokens.css),
// claros y con las iniciales en `avatar-fg`, para que se vean sobre la barra.
// Escritos enteros para que Tailwind los encuentre.
export const AVATAR_BACKGROUNDS = [
  'bg-avatar-1',
  'bg-avatar-2',
  'bg-avatar-3',
  'bg-avatar-4',
  'bg-avatar-5',
  'bg-avatar-6',
] as const;

interface InitialsInput {
  firstName?: string;
  lastName?: string;
  email: string;
}

// Quita acentos / diacríticos manteniendo letras base.
function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

function firstAlpha(value: string): string {
  const cleaned = stripDiacritics(value).replace(/[^A-Za-z]/g, '');
  return cleaned.charAt(0);
}

/**
 * Devuelve 2 letras uppercase para representar al usuario.
 * - firstName + lastName  -> primera letra de cada uno.
 * - solo uno de los dos   -> primeras 2 letras del que exista.
 * - sin nombres           -> primeras 2 letras del local-part del email.
 */
export function getInitials(input: InitialsInput): string {
  const firstName = input.firstName?.trim();
  const lastName = input.lastName?.trim();

  if (firstName && lastName) {
    const a = firstAlpha(firstName);
    const b = firstAlpha(lastName);
    if (a && b) return (a + b).toUpperCase();
  }

  const onlyName = firstName || lastName;
  if (onlyName) {
    const cleaned = stripDiacritics(onlyName).replace(/[^A-Za-z]/g, '');
    if (cleaned.length >= 2) return cleaned.slice(0, 2).toUpperCase();
    if (cleaned.length === 1) return (cleaned + cleaned).toUpperCase();
  }

  const localPart = input.email.split('@')[0] ?? '';
  const cleanedLocal = stripDiacritics(localPart).replace(/[^A-Za-z]/g, '');
  if (cleanedLocal.length >= 2) return cleanedLocal.slice(0, 2).toUpperCase();
  if (cleanedLocal.length === 1) return (cleanedLocal + cleanedLocal).toUpperCase();

  return '??';
}

/**
 * Clase del fondo para una semilla que no cambia (el id de la cuenta, el
 * nombre de una app): la misma en cada carga. Mismo cálculo que Effetá Apps
 * (`avatarColorOf`), así una app tiene el mismo color en toda la suite.
 */
export function getAvatarColor(seed: string): (typeof AVATAR_BACKGROUNDS)[number] {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return AVATAR_BACKGROUNDS[hash % AVATAR_BACKGROUNDS.length]!;
}
